import { FormEvent, useEffect, useState } from "react";
import { Badge } from "ui/badge";
import { Button } from "ui/button";
import { Card, CardContent } from "ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import { Input } from "ui/input";
import { InputFieldset } from "ui/inputFieldset";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import { TextareaFieldset } from "ui/textareaFieldset";
import {
  Edit2,
  Plus,
  Search,
  Tags,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { toast } from "sonner";
import { usePageHeader } from "contexts/PageHeaderContext";
import { useScreenPermission } from "hooks/useScreenPermission";
import Pagination from "ui/pagination";
import {
  createVehicleType,
  updateVehicleType,
  getVehicleTypeById,
  searchVehicleTypes,
} from "services/vehicleTypeMaster";
import { FullScreenLoading } from "components/ui/loader";

export type TransportVehicleType = {
  id: string;
  code: string;
  name: string;
  description: string;
  active: boolean;
};

export type TransportRoute = {
  id: string;
  routeCode: string;
  routeName: string;
  origin: string;
  destination: string;
  distanceKm: number;
  estimatedHours: number;
  stops: string;
  description: string;
  active: boolean;
};

const emptyType = (): TransportVehicleType => ({
  id: "",
  code: "",
  name: "",
  description: "",
  active: true,
});

export function VehicleTypeMasterPage() {
  const { setPageHeader } = usePageHeader();
  const [items, setItems] = useState<TransportVehicleType[]>([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<TransportVehicleType>(emptyType());

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const { screenName, icon: ScreenIcon } = useScreenPermission();

  const [errors, setErrors] = useState({
    code: "",
    name: "",
    description: "",
  });

  useEffect(() => {
    setPageHeader({
      title: screenName,
      icon: <ScreenIcon className="h-4 w-4" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    setPageNo(1);
  }, [search]);

  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        setLoading(true);

        const response = await searchVehicleTypes({
          TypeName: search.trim() || undefined,
          PageNumber: pageNo,
          PageSize: pageSize,
        });

        if (response.success && response.data?.success) {
          const vehicleTypes: TransportVehicleType[] = response.data.data.map(
            (item) => ({
              id: item.publicId,
              code: item.code,
              name: item.typeName,
              description: item.description || "",
              active: item.isActive,
            }),
          );

          setItems(vehicleTypes);
          setTotalItems(response.data.totalRecords);
        } else {
          setItems([]);
          setTotalItems(0);

          toast.error(response.message || "Failed to load vehicle types");
        }
      } catch (error) {
        console.error("Error loading vehicle types:", error);

        setItems([]);
        setTotalItems(0);

        toast.error(getApiErrorMessage(error));
      } finally {
        setLoading(false);
      }
    };

    fetchVehicleTypes();
  }, [search, pageNo, pageSize]);

  // Handle sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn("");
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const filtered = items;

  const sortedData = [...filtered].sort((a, b) => {
    if (!sortColumn) return 0;

    let aValue: string | boolean = a[sortColumn as keyof TransportVehicleType];
    let bValue: string | boolean = b[sortColumn as keyof TransportVehicleType];

    if (typeof aValue === "string") {
      aValue = aValue.toLowerCase();
    }

    if (typeof bValue === "string") {
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) {
      return sortDirection === "asc" ? -1 : 1;
    }

    if (aValue > bValue) {
      return sortDirection === "asc" ? 1 : -1;
    }

    return 0;
  });

  const paginatedItems = sortedData;

  const showForm = async (item?: TransportVehicleType) => {
    setErrors({
      code: "",
      name: "",
      description: "",
    });

    if (!item) {
      setForm(emptyType());
      setOpen(true);
      return;
    }

    try {
      setLoading(true);

      const response = await getVehicleTypeById(item.id);

      if (response.success && response.data) {
        setForm({
          id: response.data.publicId,
          code: response.data.code,
          name: response.data.typeName,
          description: response.data.description || "",
          active: response.data.isActive,
        });

        setOpen(true);
      } else {
        toast.error(
          response.message ||
            (response.errors?.length
              ? response.errors.join(", ")
              : "Failed to load vehicle type."),
        );
      }
    } catch (error) {
      console.error("Error loading vehicle type:", error);

      toast.error(getApiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const VEHICLE_CODE_REGEX = /^[A-Z0-9]{1,10}$/;
  const VEHICLE_NAME_REGEX = /^[A-Za-z0-9 ]{1,20}$/;
  const VEHICLE_DESCRIPTION_REGEX = /^[A-Za-z0-9 ]*$/;

  const getApiErrorMessage = (error: unknown): string => {
    const apiError = error as {
      response?: {
        data?: {
          message?: string;
          errors?: string[];
        };
      };
    };

    const data = apiError.response?.data;

    if (data?.message) {
      return data.message;
    }

    if (data?.errors?.length) {
      return data.errors.join(", ");
    }

    return "Something went wrong. Please try again.";
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();

    const newErrors = {
      code: "",
      name: "",
      description: "",
    };

    // Code validation
    if (!form.code.trim()) {
      newErrors.code = "Type Code is required";
    } else if (!VEHICLE_CODE_REGEX.test(form.code.trim())) {
      newErrors.code =
        "Type Code can contain only letters and numbers (maximum 10 characters).";
    }

    // Vehicle Type Name validation
    if (!form.name.trim()) {
      newErrors.name = "Vehicle Type Name is required";
    } else if (!VEHICLE_NAME_REGEX.test(form.name.trim())) {
      newErrors.name =
        "Vehicle Type Name can contain only letters, numbers and spaces (maximum 20 characters).";
    }

    // Description validation
    if (form.description && !VEHICLE_DESCRIPTION_REGEX.test(form.description)) {
      newErrors.description =
        "Description can contain only letters, numbers and spaces.";
    }

    // Duplicate code validation
    const codeExists = items.some(
      (item) =>
        item.code.toLowerCase() === form.code.trim().toLowerCase() &&
        item.id !== form.id,
    );

    if (codeExists) {
      newErrors.code = "Type Code already used for another vehicle type.";
    }

    setErrors(newErrors);

    // Stop save if validation failed
    if (newErrors.code || newErrors.name || newErrors.description) {
      return;
    }

    try {
      setLoading(true);

      const payload = {
        typeName: form.name.trim(),
        code: form.code.trim(),
        description: form.description.trim(),
        isActive: form.active,
      };

      let response;

      // EDIT
      if (form.id) {
        response = await updateVehicleType(form.id, payload);
      }
      // ADD
      else {
        response = await createVehicleType(payload);
      }

      if (response.success) {
        toast.success(
          response.message ||
            (form.id
              ? "Vehicle type updated successfully."
              : "Vehicle type added successfully."),
        );

        setOpen(false);

        setForm(emptyType());

        setErrors({
          code: "",
          name: "",
          description: "",
        });

        const refreshedResponse = await searchVehicleTypes({
          TypeName: search.trim() || undefined,
          PageNumber: pageNo,
          PageSize: pageSize,
        });
 
        if (refreshedResponse.success && refreshedResponse.data?.success) {
          const vehicleTypes: TransportVehicleType[] =
            refreshedResponse.data.data.map((item) => ({
              id: item.publicId,
              code: item.code,
              name: item.typeName,
              description: item.description || "",
              active: item.isActive,
            }));

          setItems(vehicleTypes);
          setTotalItems(refreshedResponse.data.totalRecords);
        }
      } else {
        toast.error(
          response.message ||
            (response.errors?.length
              ? response.errors.join(", ")
              : form.id
                ? "Failed to update vehicle type."
                : "Failed to create vehicle type."),
        );
      }
    } catch (error) {
      console.error(
        form.id
          ? "Error updating vehicle type:"
          : "Error creating vehicle type:",
        error,
      );

      toast.error(getApiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex w-full flex-wrap items-center gap-2 sm:flex-nowrap">
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                className="h-10 pl-9 shadow-sm"
                placeholder="Search by vehicle type name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Button
              className="bg-[#5B9BD5] text-white"
              onClick={() => showForm()}
            >
              <Plus className="mr-2 h-4 w-4" /> Add Vehicle Type
            </Button>
          </div>
        </div>
        <Card
          className="border-[#5B9BD5]/30 bg-white"
          style={{ gap: 0, marginTop: "10px" }}
        >
          <CardContent style={{ padding: "0px" }}>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#E3F2FD]">
                    <TableHead
                      className="px-4 py-3 text-xs text-[#5B9BD5] cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("code")}
                    >
                      <div className="flex items-center gap-1">
                        Code
                        {sortColumn === "code" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      className="px-4 py-3 text-xs text-[#5B9BD5] cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center gap-1">
                        Vehicle Type Name
                        {sortColumn === "name" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead className="px-4 py-3 text-xs text-[#5B9BD5]">
                      Description
                    </TableHead>
                    <TableHead className="px-4 py-3 text-xs text-[#5B9BD5]">
                      Status
                    </TableHead>
                    <TableHead className="px-4 py-3 text-right text-xs text-[#5B9BD5]">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedData.map((item) => (
                    <TableRow key={item.id} className="hover:bg-sky-50/30">
                      <TableCell className="px-4 py-2 text-xs font-medium text-[#5B9BD5]">
                        {item.code}
                      </TableCell>
                      <TableCell className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-sky-100 to-blue-100">
                            <Tags className="h-4 w-4 text-[#5B9BD5]" />
                          </div>
                          <span className="text-xs font-medium text-gray-700">
                            {item.name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-md px-4 py-2 text-xs text-gray-600">
                        {item.description || "—"}
                      </TableCell>
                      <TableCell className="px-4 py-2">
                        <Badge
                          className={
                            item.active
                              ? "bg-green-100 text-green-700 border-green-200"
                              : "bg-gray-100 text-gray-700 border-gray-200"
                          }
                        >
                          {item.active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-2 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => showForm(item)}
                          className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!filtered.length && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="py-10 text-center text-sm font-medium text-[#5B9BD5]"
                      >
                        No vehicle types found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-3 p-4">
              {filtered.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Tags className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No vehicle types found</p>
                </div>
              ) : (
                paginatedItems.map((item) => (
                  <Card
                    key={item.id}
                    className="border-[#5B9BD5]/20 hover:border-[#5B9BD5]/40 transition-colors"
                  >
                    <CardContent style={{ padding: "14px" }}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center">
                            <Tags className="w-5 h-5 text-[#5B9BD5]" />
                          </div>

                          <div>
                            <Badge
                              variant="outline"
                              className="bg-sky-50 border-[#5B9BD5]/30 text-[#5B9BD5] font-mono text-xs mt-1"
                            >
                              {item.code}
                            </Badge>
                            <p className="font-semibold text-gray-900">
                              {item.name}
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-2 justify-end">
                          <Badge
                            variant="outline"
                            className={
                              item.active
                                ? "bg-green-100 text-green-700 border-green-200 text-xs"
                                : "bg-gray-100 text-gray-700 border-gray-200 text-xs"
                            }
                          >
                            {item.active ? "Active" : "Inactive"}
                          </Badge>

                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => showForm(item)}
                            className="text-[#5B9BD5] hover:text-[#4A8BC2] hover:bg-sky-50 h-8 w-8 p-0"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <p className="text-sm text-gray-700 mt-2">
                        {item.description || "—"}
                      </p>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </CardContent>

          <Pagination
            totalItems={totalItems}
            itemsPerPage={pageSize}
            currentPage={pageNo}
            onPageChange={(page) => setPageNo(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNo(1);
            }}
            label="vehicle types"
          />
        </Card>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-[600px] bg-white">
            <form onSubmit={save}>
              <DialogHeader>
                <DialogTitle>
                  {form.id ? "Edit Vehicle Type" : "Add Vehicle Type"}
                </DialogTitle>
                <DialogDescription>
                  Maintain the basic vehicle type information used across
                  transport.
                </DialogDescription>
              </DialogHeader>
              <div className="transport-floating-grid grid grid-cols-1 gap-4 py-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <InputFieldset
                    label="Type Code"
                    required
                    value={form.code}
                    placeholder="e.g., COACH, BUS"
                    onChange={(e) => {
                      const value = e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z0-9]/g, "")
                        .slice(0, 10);

                      setForm({
                        ...form,
                        code: value,
                      });

                      setErrors({
                        ...errors,
                        code: "",
                      });
                    }}
                    className={`text-sm ${
                      errors.code ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />

                  {errors.code && (
                    <p className="text-red-500 text-xs">{errors.code}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Vehicle Type Name"
                    required
                    value={form.name}
                    placeholder="e.g., Coach"
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/[^A-Za-z0-9 ]/g, "")
                        .slice(0, 20);

                      setForm({
                        ...form,
                        name: value,
                      });

                      setErrors({
                        ...errors,
                        name: "",
                      });
                    }}
                    className={`text-sm ${
                      errors.name ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />

                  {errors.name && (
                    <p className="text-red-500 text-xs">{errors.name}</p>
                  )}
                </div>
                <div className="sm:col-span-2 space-y-2">
                  <TextareaFieldset
                    label="Description"
                    rows={2}
                    value={form.description}
                    placeholder="Brief description of the vehicle type"
                    onChange={(e) => {
                      const value = e.target.value.replace(
                        /[^A-Za-z0-9 ]/g,
                        "",
                      );

                      setForm({
                        ...form,
                        description: value,
                      });

                      setErrors({
                        ...errors,
                        description: "",
                      });
                    }}
                  />

                  {errors.description && (
                    <p className="text-red-500 text-xs">{errors.description}</p>
                  )}
                </div>
                <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) =>
                      setForm({ ...form, active: e.target.checked })
                    }
                  />{" "}
                  Active type
                </label>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setOpen(false);
                    setErrors({
                      code: "",
                      name: "",
                      description: "",
                    });
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#5B9BD5] text-white">
                  {form.id ? "Update Vehicle Type" : "Save Vehicle Type"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
